import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { AccountDetails, PaginatedOauthGrants } from '@/types'
import { useJwtStore } from './jwt'

/**
 * Whether the account section offers the Connections page: the account may
 * connect agents, or already holds a grant from before that changed. Never in
 * a team or reseller context, or for an unverified email, whose sessions the
 * grant routes refuse.
 */
export const useAgentConnectionsStore = defineStore('agentConnections', () => {
  const visible = ref(false)
  // Only the latest refresh may set the answer: one still in flight when the
  // session switches context would otherwise land after it.
  let latest = 0

  async function refresh(): Promise<void> {
    const run = ++latest
    const jwt = useJwtStore().getJwtContent
    if (!jwt || jwt.acting_as_account_id !== undefined || jwt.limited) {
      visible.value = false
      return
    }
    const settle = (shown: boolean): void => {
      if (run === latest) visible.value = shown
    }

    try {
      // Only the fields read here: a mismatch elsewhere must not hide the page.
      const account = AccountDetails.pick({ agent_access: true }).safeParse(
        (await useAxios('GET', '/account', true)).data
      )
      if (account.success && account.data.agent_access) {
        settle(true)
        return
      }
      const grants = PaginatedOauthGrants.pick({ total: true }).safeParse(
        (await useAxios('GET', '/account/grants?limit=1', true)).data
      )
      settle(grants.success && grants.data.total > 0)
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'AgentConnections', reportNetworkErrors: false })
      settle(false)
    }
  }

  return { visible, refresh }
})
